import * as React from "react"
import * as DialogPrimitive from "@radix-ui/react-dialog"
import { X } from "lucide-react"

import { cn } from "@/lib/utils"

// Context: marca cuando un cierre viene de una acción explícita del usuario
// (X, Cancelar, Guardar, Escape). Cualquier otro cierre auto-disparado por
// Radix (teclado virtual móvil, autofill, blur/focus, etc.) se ignora.
const DialogUserCloseContext = React.createContext(null);

const Dialog = ({ open, onOpenChange, ...props }) => {
  const intentRef = React.useRef(false);
  const markIntent = React.useCallback(() => { intentRef.current = true; }, []);
  const filteredOnOpenChange = React.useCallback((next) => {
    if (next === true) {
      onOpenChange && onOpenChange(true);
      return;
    }
    // next === false: solo propaga si hubo intención explícita
    if (intentRef.current) {
      intentRef.current = false;
      onOpenChange && onOpenChange(false);
    }
    // si no: ignoramos el cierre accidental
  }, [onOpenChange]);
  return (
    <DialogUserCloseContext.Provider value={markIntent}>
      <DialogPrimitive.Root open={open} onOpenChange={filteredOnOpenChange} {...props} />
    </DialogUserCloseContext.Provider>
  );
};

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = React.forwardRef(({ onClick, ...props }, ref) => {
  const markIntent = React.useContext(DialogUserCloseContext);
  return (
    <DialogPrimitive.Close
      ref={ref}
      onClick={(e) => { markIntent && markIntent(); onClick && onClick(e); }}
      {...props}
    />
  );
});
DialogClose.displayName = "DialogClose";

const DialogOverlay = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-50 bg-black/80  data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className
    )}
    {...props} />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const DialogContent = React.forwardRef((
  { className, children, onPointerDownOutside, onInteractOutside, onFocusOutside, onEscapeKeyDown, ...props },
  ref,
) => {
  const markIntent = React.useContext(DialogUserCloseContext);
  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(
          "fixed left-[50%] top-[50%] z-50 grid w-full max-w-lg translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%] data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg",
          className
        )}
        onPointerDownOutside={(e) => { e.preventDefault(); onPointerDownOutside && onPointerDownOutside(e); }}
        onFocusOutside={(e) => { e.preventDefault(); onFocusOutside && onFocusOutside(e); }}
        onInteractOutside={(e) => { e.preventDefault(); onInteractOutside && onInteractOutside(e); }}
        onEscapeKeyDown={(e) => { markIntent && markIntent(); onEscapeKeyDown && onEscapeKeyDown(e); }}
        {...props}>
        {children}
        <DialogPrimitive.Close
          onClick={() => markIntent && markIntent()}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPortal>
  );
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)}
    {...props} />
)
DialogHeader.displayName = "DialogHeader"

const DialogFooter = ({
  className,
  ...props
}) => (
  <div
    className={cn("flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2", className)}
    {...props} />
)
DialogFooter.displayName = "DialogFooter"

const DialogTitle = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold leading-none tracking-tight", className)}
    {...props} />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props} />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogTrigger,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
